<script>
import CommunityUserRoleForm from "@/components/Community/UserRoleForm.vue";
import IconButton from "@/components/shared/IconButton.vue";
import PaginatedTable from "@/components/shared/PaginatedTable.vue";
import { Column } from "@/components/shared/PaginatedTableColumns";
import ValidatedButton from "@/components/shared/ValidatedButton.vue";
import { capitalize } from "@/helpers/filters";
import { userLink } from "@/helpers/links";
import { canManageCommunityRoles } from "@/helpers/permissions/communities.js";
import { del, post } from "@/requests/server";

export default {
  name: "CommunityUserRolesList",
  components: {
    IconButton,
    PaginatedTable,
    CommunityUserRoleForm,
    ValidatedButton,
  },
  props: {
    community: {
      type: Object,
      required: false,
      default: undefined,
    },
    user: {
      type: Object,
      required: false,
      default: undefined,
    },
  },
  data() {
    return {
      editedRole: null,
    };
  },
  computed: {
    accessingUser() {
      return this.$store.state.user;
    },
    canAddRole() {
      return canManageCommunityRoles(this.accessingUser);
    },
    columns() {
      let columns = [
        // Necessary to have a unique id for rows in the list.
        new Column("id", capitalize(this.$t("lists.id")), "id", {
          showByDefault: false,
        }),
      ];

      if (!this.user) {
        columns.push(
          Column.withRelation(
            "user.full_name",
            this.$t("user_roles.fields.user"),
            {
              relation: "users",
              label: "full_name",
              field: "id",
            },
            "user_id",
            {
              urlFct: (userRole) => userLink(userRole.user),
            }
          )
        );
      }

      columns.push(
        Column.withSelect("role", this.$t("communities.fields.user_roles.role.title"), [
          { value: null, label: this.$t("communities.fields.user_roles.role.options.null.all") },
          {
            value: "admin",
            label: this.$t("communities.fields.user_roles.role.options.admin"),
            variant: "danger",
          },
        ])
      );

      columns.push(
        Column.withRelation(
          "granted_by_user.full_name",
          this.$t("user_roles.fields.granted_by"),
          {
            relation: "users",
            label: "full_name",
            field: "id",
            params: {
              for: "profile",
            },
          },
          "granted_by_user_id",
          {
            urlFct: (userRole) => userLink(userRole.granted_by_user),
          }
        )
      );

      return columns;
    },
    listEndpoint() {
      if (this.user && this.community) {
        return `/communities/${this.community.id}/users/${this.user.id}/roles`;
      } else if (this.community) {
        return `/communities/${this.community.id}/roles`;
      }
      return "";
    },
  },
  methods: {
    canDeleteRole(role) {
      // Don't allow deleting own admin role.
      if (role.role === "admin" && role.user_id === this.accessingUser.id) {
        return false;
      }

      return canManageCommunityRoles(this.accessingUser);
    },
    beginEditRole(role = null) {
      if (!role) {
        this.editedRole = {
          community_id: this.community?.id,
          community: this.community,
          user_id: this.user?.id,
          user: this.user,
        };
      } else {
        this.editedRole = role;
      }

      // Can only edit role if creating a new one. Otherwise, one must delete and add a new role.
      this.$refs.communityUserRoleEditModal.canEditRole = !this.editedRole.id;
      this.$refs.communityUserRoleEditModal.canEditUser = !this.editedRole.user;

      this.$refs.communityUserRoleEditModal.show();
    },
    async saveRole(editedRole) {
      // Data we get back from the server after saving.
      let updatedRole = null;
      if (editedRole.id) {
        // Roles have no editable parameters atm so it is not implemented.
      } else {
        // A new role is created.
        let endpoint = "";
        if (this.user && this.community) {
          endpoint = `/communities/${this.community.id}/users/${this.user.id}/roles`;
        } else if (this.community) {
          endpoint = `/communities/${this.community.id}/roles`;
        } else {
          return;
        }

        const { data } = await post(
          endpoint,
          {
            community_id: editedRole.community_id,
            user_id: editedRole.user_id,
            role: editedRole.role,
          },
          {
            notifications: {
              action: this.$t("user_roles.notifications.add_role.action"),
              onSuccess: this.$t("user_roles.notifications.add_role.success"),
            },
          }
        );
        updatedRole = data;

        // Emit added so the parent can account for the change.
        this.$emit("added", updatedRole);
      }

      this.$refs.communityUserRolesTable.refresh();
    },
    async deleteRole(role) {
      await del(`/communities/roles/${role.id}`, {
        notifications: {
          action: this.$t("user_roles.notifications.delete_role.action"),
          onSuccess: this.$t("user_roles.notifications.delete_role.success"),
        },
      });

      this.$refs.communityUserRolesTable.refresh();

      // Emit deleted so the parent can account for the change.
      this.$emit("deleted", role);
    },
  },
};
</script>
<template>
  <div>
    <paginated-table
      ref="communityUserRolesTable"
      :endpoint="listEndpoint"
      :columns="columns"
      :show-generate-csv="false"
      v-bind="$attrs"
    >
      <template #head-buttons>
        <icon-button v-if="canAddRole" role="add" @click="() => beginEditRole()">{{
          $t("user_roles.actions.add")
        }}</icon-button>
      </template>

      <template #cell(actions)="{ item }">
        <validated-button
          v-if="canDeleteRole(item)"
          :action="async () => deleteRole(item)"
          :question="
            $t('user_roles.delete_role_confirmation', {
              role: $t(`communities.fields.user_roles.role.options.${item.role}`),
            })
          "
          :label="$t('user_roles.forms.revoke')"
          icon="trash"
          size="sm"
          variant="ghost-danger"
          :show-button-label="false"
        />
        <span v-else />
      </template>
    </paginated-table>

    <community-user-role-form
      ref="communityUserRoleEditModal"
      :value="editedRole"
      @save="saveRole"
    />
  </div>
</template>

<style lang="scss"></style>
